import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { RealtimeDBService } from '../../firebase/unifiedDataService';
import { useTheme, ThemeToggle } from '../../context/ThemeContext';
import { SmartTextRenderer } from '../../components/ui/MathRenderer';
import { QuestionMedia } from '../../components/ui/QuestionMedia';
import QuestionStatsBadge from '../../components/student/QuestionStatsBadge';
import { Logger } from '../../utils/logger';

const log = Logger.create('PracticeQuizView');

/**
 * PracticeQuizView - Allows students to practice past quizzes
 * Self-paced, no timer, immediate feedback, no stats recording
 */
function PracticeQuizView() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useUnifiedAuth();
  const { theme } = useTheme();
  
  const [questions, setQuestions] = useState(null);
  const [settings, setSettings] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);
  const [results, setResults] = useState([]); // Track results per question
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load quiz data
  useEffect(() => {
    const loadQuiz = async () => {
      try {
        // Check if we have practice quiz data in sessionStorage
        const storedQuestions = sessionStorage.getItem('practiceQuizQuestions');
        const storedSettings = sessionStorage.getItem('practiceQuizSettings');
        
        if (storedQuestions && storedSettings) {
          // Load from sessionStorage (generated practice quiz)
          const questions = JSON.parse(storedQuestions);
          const settings = JSON.parse(storedSettings);
          
          setQuestions(questions);
          setSettings({
            ...settings,
            title: `Übung: ${settings.courseName}`,
            isPracticeMode: true
          });
          setIsLoading(false);
          
          // Clear sessionStorage
          sessionStorage.removeItem('practiceQuizQuestions');
          sessionStorage.removeItem('practiceQuizSettings');
          return;
        }
        
        // Otherwise, load from Firebase (historical quiz practice)
        // Get teacherUserId from URL params if present, otherwise try to find in quiz history
        const teacherUserId = searchParams.get('teacherId');
        
        let questionsData = null;
        let settingsData = null;
        
        if (teacherUserId) {
          // New format: quiz_instances/{teacherUserId}/{sessionId}
          questionsData = await RealtimeDBService.get(`quiz_instances/${teacherUserId}/${sessionId}/questions`);
          settingsData = await RealtimeDBService.get(`quiz_instances/${teacherUserId}/${sessionId}/settings`);
        } else {
          // First try new format by looking up session code or trying old format
          // Try legacy sessions path first
          questionsData = await RealtimeDBService.get(`sessions/${sessionId}/questions`);
          
          if (!questionsData) {
            // Session not found in old format, could be a new format instanceId
            // We need the teacherUserId to look it up - show error
            setError('Quiz nicht gefunden. Bitte versuche es über die Historie erneut.');
            setIsLoading(false);
            return;
          }
          
          settingsData = await RealtimeDBService.get(`sessions/${sessionId}/settings`);
        }
        
        if (!questionsData) {
          setError('Quiz nicht gefunden');
          setIsLoading(false);
          return;
        }
        
        setQuestions(questionsData);
        setSettings(settingsData || {});
        
        setIsLoading(false);
      } catch (err) {
        log.error('Error loading quiz:', err);
        setError('Fehler beim Laden des Quiz');
        setIsLoading(false);
      }
    };
    
    loadQuiz();
  }, [sessionId, searchParams]);
  
  // Handle option selection
  const handleSelectOption = (index) => {
    if (showAnswer) return;
    
    const question = questions[currentQuestion];
    const isMultiple = question.allowMultiple || (question.correctAnswerIndices && question.correctAnswerIndices.length > 1);
    
    if (isMultiple) {
      setSelectedOptions(prev => 
        prev.includes(index) 
          ? prev.filter(i => i !== index) 
          : [...prev, index]
      );
    } else {
      setSelectedOptions([index]);
    }
  };
  
  // Check answer
  const handleCheckAnswer = () => {
    if (selectedOptions.length === 0) return;
    
    const question = questions[currentQuestion];
    const correctIndices = question.correctAnswerIndices || [question.correctAnswerIndex];
    const isMultiple = question.allowMultiple || (question.correctAnswerIndices && question.correctAnswerIndices.length > 1);
    
    let isCorrect;
    if (isMultiple) {
      const selectedSet = new Set(selectedOptions);
      const correctSet = new Set(correctIndices);
      isCorrect = selectedSet.size === correctSet.size && 
                  [...selectedSet].every(idx => correctSet.has(idx));
    } else {
      isCorrect = correctIndices.includes(selectedOptions[0]);
    }
    
    // Store result
    setResults(prev => [...prev, { questionIndex: currentQuestion, isCorrect, selectedOptions }]);
    setShowAnswer(true);
  };
  
  // Go to next question
  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOptions([]);
      setShowAnswer(false);
    } else {
      setIsComplete(true);
    }
  };
  
  // Restart quiz
  const handleRestart = () => {
    setCurrentQuestion(0);
    setSelectedOptions([]);
    setShowAnswer(false);
    setResults([]);
    setIsComplete(false);
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Lade Übungsquiz...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">
            <svg className="w-20 h-20 mx-auto text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Fehler</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Link
            to="/student/history"
            className="inline-block px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg theme-transition"
          >
            Zur Quiz-Historie
          </Link>
        </div>
      </div>
    );
  }
  
  // Quiz complete - show results
  if (isComplete) {
    const correctCount = results.filter(r => r.isCorrect).length;
    const totalCount = questions.length;
    const percentage = Math.round((correctCount / totalCount) * 100);
    
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Übung abgeschlossen</h1>
            <ThemeToggle />
          </div>
        </header>
        
        <main className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
            <div className={`text-6xl mb-4 ${percentage >= 80 ? '' : percentage >= 50 ? '' : ''}`}>
              {percentage >= 80 ? 'Ausgezeichnet!' : percentage >= 50 ? 'Gut gemacht!' : 'Weiter üben!'}
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {percentage >= 80 ? 'Hervorragend!' : percentage >= 50 ? 'Gut gemacht!' : 'Weiter üben!'}
            </h2>
            
            <div className="text-4xl font-bold mb-6">
              <span className={percentage >= 80 ? 'text-green-600' : percentage >= 50 ? 'text-yellow-600' : 'text-red-600'}>
                {percentage}%
              </span>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Du hast {correctCount} von {totalCount} Fragen richtig beantwortet.
            </p>
            
            {/* Question Summary */}
            <div className="flex justify-center gap-2 mb-8 flex-wrap">
              {results.map((result, index) => (
                <div
                  key={index}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
                    result.isCorrect ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  title={`Frage ${index + 1}: ${result.isCorrect ? 'Richtig' : 'Falsch'}`}
                >
                  {index + 1}
                </div>
              ))}
            </div>
            
            {/* Detailed Question Results */}
            <div className="mb-8 space-y-3 text-left">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 text-center">
                Frage-Details
              </h3>
              {results.map((result, index) => {
                const question = questions[index];
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 ${
                      result.isCorrect
                        ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-700'
                        : 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white mb-1">
                          Q{index + 1}: <SmartTextRenderer text={question.question} inline />
                        </div>
                        <div className={`text-sm font-semibold ${
                          result.isCorrect ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                        }`}>
                          {result.isCorrect ? '✓ RICHTIG' : '✗ FALSCH'}
                        </div>
                      </div>
                      {result.isCorrect ? (
                        <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                    
                    {/* Question Stats */}
                    {question.timesAnswered > 0 && (
                      <div className="mt-2 flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                        <span>• {question.timesAnswered}x gesehen</span>
                        <span>• {question.timesCorrect}x richtig ({Math.round((question.timesCorrect / question.timesAnswered) * 100)}%)</span>
                        {question.averageTimeSeconds && (
                          <span>• Ø {question.averageTimeSeconds.toFixed(1)}s</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleRestart}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg theme-transition flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Erneut üben
              </button>
              <Link
                to="/student/dashboard"
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-lg theme-transition hover:bg-gray-300 dark:hover:bg-gray-600 text-center"
              >
                Zum Dashboard
              </Link>
              <Link
                to="/student/history"
                className="px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg theme-transition hover:border-gray-400 dark:hover:border-gray-500 text-center"
              >
                Zur Historie
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  // Current question
  const question = questions[currentQuestion];
  const correctIndices = question.correctAnswerIndices || [question.correctAnswerIndex];
  const isMultiple = question.allowMultiple || (question.correctAnswerIndices && question.correctAnswerIndices.length > 1);
  const quizName = settings?.quizName || 'Quiz';
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/student/history"
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ← Zurück
            </Link>
            <div>
              <h1 className="font-semibold text-gray-900 dark:text-white">{quizName}</h1>
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Übungsmodus</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {currentQuestion + 1} / {questions.length}
            </span>
            <ThemeToggle />
          </div>
        </div>
      </header>
      
      {/* Progress Bar */}
      <div className="bg-gray-200 dark:bg-gray-700 h-1">
        <div
          className="bg-green-500 h-full transition-all duration-300"
          style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
        />
      </div>
      
      {/* Question */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          {/* Question Stats Badge */}
          {question.timesAnswered > 0 && (
            <div className="mb-4">
              <QuestionStatsBadge
                timesAnswered={question.timesAnswered}
                timesCorrect={question.timesCorrect}
              />
            </div>
          )}
          
          {/* Question Text */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Frage {currentQuestion + 1}
            </h2>
            <div className="text-gray-700 dark:text-gray-300">
              <SmartTextRenderer text={question.question} />
            </div>
            
            {/* Question Media */}
            {question.media && (
              <div className="mt-4">
                <QuestionMedia media={question.media} />
              </div>
            )}
            
            {/* Question Source */}
            {question.sourceQuizName && (
              <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Quelle: {question.sourceQuizName}
                {question.lastAnswered && (
                  <span className="text-gray-400 dark:text-gray-500">
                    ({new Date(question.lastAnswered).toLocaleDateString('de-DE')})
                  </span>
                )}
              </div>
            )}
            
            {isMultiple && (
              <p className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                Mehrere Antworten möglich
              </p>
            )}
          </div>
          
          {/* Options */}
          <div className="space-y-3 mb-6">
            {question.options.map((option, index) => {
              const isSelected = selectedOptions.includes(index);
              const isCorrect = correctIndices.includes(index);
              
              let className = 'w-full text-left p-4 rounded-lg border-2 theme-transition ';
              
              if (showAnswer) {
                if (isCorrect) {
                  className += 'bg-green-50 dark:bg-green-900/30 border-green-500 text-green-800 dark:text-green-200';
                } else if (isSelected && !isCorrect) {
                  className += 'bg-red-50 dark:bg-red-900/30 border-red-500 text-red-800 dark:text-red-200';
                } else {
                  className += 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400';
                }
              } else {
                if (isSelected) {
                  className += 'bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-800 dark:text-blue-200';
                } else {
                  className += 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-500';
                }
              }
              
              return (
                <button
                  key={index}
                  onClick={() => handleSelectOption(index)}
                  disabled={showAnswer}
                  className={className}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{String.fromCharCode(65 + index)})</span>
                    <SmartTextRenderer text={option} />
                    {showAnswer && isCorrect && (
                      <svg className="ml-auto w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {showAnswer && isSelected && !isCorrect && (
                      <svg className="ml-auto w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Explanation (if available and answer shown) */}
          {showAnswer && question.explanation && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">Erklärung:</h4>
              <p className="text-blue-700 dark:text-blue-300 text-sm">
                <SmartTextRenderer text={question.explanation} />
              </p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            {!showAnswer ? (
              <button
                onClick={handleCheckAnswer}
                disabled={selectedOptions.length === 0}
                className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Antwort prüfen
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg theme-transition"
              >
                {currentQuestion < questions.length - 1 ? 'Nächste Frage →' : 'Ergebnis anzeigen'}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default PracticeQuizView;
